import {
  SettingsPermissions,
  UserPermissions,
} from "@redotech/account-common/permissions";
import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { ReclaimFlagKey } from "@redotech/feature-flags-keys-reclaim/keys";
import { RedoProduct } from "@redotech/redo-common-model/redo-product";
import { t } from "@redotech/redo-i18n";
import { IntegrationKind } from "@redotech/redo-integrations-core";
import { PermissionHandle } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { IntegrationStatusKey } from "@redotech/redo-merchant-integrations-rpc/schema/returns-rpc/get-all-integration-statuses";
import type { Gen2IntegrationKind } from "@redotech/redo-return-core-common/integrations/gen2-integration-kinds";
import type { CarrierBrandSlug } from "./carrier-brands";
import {
  CatalogAvailabilitySetting,
  IntegrationCatalogEntry,
  IntegrationTypeCategory,
} from "./catalog-types";

const R = SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS;
const S = SettingsPermissions.SET_SUPPORT_SETTINGS;
const M = SettingsPermissions.SET_MARKETING_SETTINGS;
const CA = SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS;
const OF = SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS;
const CO = SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS;
const G = SettingsPermissions.SET_GENERAL_SETTINGS;
const SH = SettingsPermissions.SET_SHIPPING_SETTINGS;

const erpWms3pl: IntegrationCatalogEntry[] = [
  {
    slug: "aims360",
    name: "Aims360",
    get description() {
      return t(
        "Connect your {{integrationName}} account to automatically sync returns",
        { integrationName: "Aims360" },
      );
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.AIMS360 },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Aims360"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/aims360",
  },
  {
    slug: "apparel-magic",
    name: "ApparelMagic",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "ApparelMagic",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    // Split-doc vendor: returns and OMS live in separate docs, and the hub key
    // only sees the returns one — an OMS-only merchant must still read connected.
    status: {
      source: "gen2AnyKind",
      kinds: ["ApparelMagic", "Oms-ApparelMagic"],
    },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["ApparelMagic", "Oms-ApparelMagic"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/apparel-magic",
  },
  {
    slug: "blue-cherry",
    name: "BlueCherry",
    get description() {
      return t(
        "Connect your {{integrationName}} WMS account to {{productName}}",
        { integrationName: "BlueCherry", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.BLUE_CHERRY },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["BlueCherry"],
    gen3Kinds: [],
  },
  {
    slug: "controlport",
    name: "ControlPort",
    get description() {
      return t(
        "Connect your {{integrationName}} warehouse to {{productName}}",
        { integrationName: "ControlPort", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.CONTROLPORT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ControlPort"],
    gen3Kinds: [],
  },
  {
    slug: "mintsoft",
    name: "Mintsoft",
    get description() {
      return t(
        "Connect your {{integrationName}} WMS account to {{productName}}",
        { integrationName: "Mintsoft", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.MINTSOFT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Mintsoft"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/mintsoft",
  },
  {
    slug: "blue-yonder",
    name: "Blue Yonder",
    get description() {
      return t(
        "Connect your {{integrationName}} WMS account to {{productName}}",
        { integrationName: "Blue Yonder", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.BLUEYONDER },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["BlueYonder"],
    gen3Kinds: [],
  },
  {
    slug: "brightpearl",
    name: "Brightpearl",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Brightpearl",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.BRIGHTPEARL },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Brightpearl"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/brightpearl",
  },
  {
    slug: "cin7-omni",
    name: "Cin7 Omni",
    get description() {
      return t(
        "Connect your {{integrationName}} account to sync returns and credit notes",
        { integrationName: "Cin7 Omni" },
      );
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.CIN7_OMNI },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Cin7Omni"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "finale",
    name: "Finale Inventory",
    get description() {
      return t(
        "Read inventory levels from {{integrationName}} so the OMS gates on your real stock",
        { integrationName: "Finale" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "Finale" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Finale"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "cloudx",
    name: "CloudX/Bergen Logistics",
    get description() {
      return t(
        "Connect your {{integrationName}}/{{integrationName2}} account to {{productName}}",
        {
          integrationName: "CloudX",
          integrationName2: "Bergen Logistics",
          productName: "Redo",
        },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.CLOUDX },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["CloudX"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/cloudx",
  },
  {
    slug: "deposco",
    name: "Deposco",
    get description() {
      return t(
        "Connect your {{integrationName}} WMS account to automatically sync return RMAs",
        { integrationName: "Deposco" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.DEPOSCO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Deposco"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/deposco",
    isBeta: true,
  },
  {
    slug: "ecwid",
    name: "Ecwid",
    get description() {
      return t(
        "Create {{integrationName}} orders automatically for returns, claims, or warranties.",
        { integrationName: "Ecwid" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.ECWID },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Ecwid"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/ecwid",
    isBeta: true,
  },
  {
    slug: "flexport",
    name: "Flexport",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Flexport",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.FLEXPORT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Flexport"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/flexport",
  },
  {
    slug: "netsuite",
    name: "NetSuite",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "NetSuite",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    // Split-doc vendor: returns and OMS live in separate docs, and the hub key
    // only sees the returns one — an OMS-only merchant must still read connected.
    status: { source: "gen2AnyKind", kinds: ["NetSuite", "Oms-NetSuite"] },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["NetSuite", "Oms-NetSuite"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/netsuite",
  },
  {
    slug: "shipbob",
    name: "ShipBob",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "ShipBob",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPBOB },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ShipBob"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipbob",
  },
  {
    slug: "shipedge",
    name: "ShipEdge",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "ShipEdge",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPEDGE },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Shipedge"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipedge",
  },
  {
    slug: "shiphero",
    name: "ShipHero",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "ShipHero",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPHERO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ShipHero"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shiphero",
  },
  {
    slug: "indigo8",
    name: "Indigo8",
    get description() {
      return t(
        "Connect your {{integrationName}} account to sync returns automatically.",
        { integrationName: "Indigo8" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.INDIGO8 },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Indigo8"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "odoo",
    name: "Odoo",
    get description() {
      return t(
        "Connect your {{integrationName}} account to create credit notes for returns automatically.",
        { integrationName: "Odoo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.ODOO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Odoo"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "shipmonk",
    name: "ShipMonk",
    get description() {
      return t(
        "Connect your {{integrationName}} account to sync returns automatically.",
        { integrationName: "ShipMonk" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SHIPMONK },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ShipMonk"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipmonk",
  },
  {
    slug: "masonhub",
    name: "MasonHub",
    get description() {
      return t(
        "Connect your {{integrationName}} account to sync returns automatically.",
        { integrationName: "MasonHub" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.MASONHUB },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Masonhub"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/masonhub",
    isBeta: true,
  },
  {
    slug: "linnworks",
    name: "Linnworks",
    get description() {
      return t(
        "Connect {{integrationName}} directly to {{productName}} for order fulfillment",
        { integrationName: "Linnworks", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "LinnworksDirect" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["LinnworksDirect"],
    gen3Kinds: [],
  },
  {
    slug: "linx",
    name: "Linx",
    get description() {
      return t(
        "Send labeled {{productName}} orders to {{integrationName}} production management",
        { productName: "Redo", integrationName: "Linx" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "Linx" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Linx"],
    gen3Kinds: [],
  },
  {
    slug: "luminous",
    name: "Luminous",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Luminous",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.LUMINOUS },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Luminous"],
    gen3Kinds: [],
  },
  {
    slug: "seko",
    name: "Seko",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Seko",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SEKO },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Seko"],
    gen3Kinds: [],
  },
  {
    slug: "trackstar",
    name: "TrackStar",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "TrackStar",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.TRACKSTAR },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["Trackstar"],
    gen3Kinds: [],
    providerDocs: [
      {
        name: "BlueBox",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/bluebox",
      },
      {
        name: "Cirro",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/cirro",
      },
      {
        name: "Nimble",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/nimble",
      },
      {
        name: "ShipStream",
        url: "https://developers.redo.com/docs/integrations/returns/3pl-wms/shipstream",
      },
    ],
  },
  {
    slug: "ryder",
    name: "RyderShip",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "RyderShip",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.RYDER },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Ryder"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/rydership",
  },
  {
    slug: "sellercloud",
    name: "Sellercloud",
    get description() {
      return t(
        "Connect your {{integrationName}} account to automatically create and sync return RMAs.",
        { integrationName: "Sellercloud" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "statusKey", key: IntegrationStatusKey.SELLERCLOUD },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Sellercloud"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "teamwork-commerce",
    name: "Teamwork Commerce",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Teamwork Commerce",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: {
      source: "statusKey",
      key: IntegrationStatusKey.TEAMWORK_COMMERCE,
    },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["TeamworkCommerce"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "cin7-core",
    name: "Cin7 Core",
    get description() {
      return t("Sync orders and fulfillments with {{integrationName}}", {
        integrationName: "Cin7 Core",
      });
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "Cin7Core" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Cin7Core"],
    gen3Kinds: [],
  },
  {
    slug: "shipstation",
    name: "ShipStation",
    get description() {
      return t("Sync orders and shipments with {{integrationName}}", {
        integrationName: "ShipStation",
      });
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen2Kind", kind: "ShipStationApi" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["ShipStationApi"],
    gen3Kinds: [],
  },
  {
    slug: "dsco",
    name: "Dsco",
    get description() {
      return t(
        "Connect your {{integrationName}} account to sync orders with {{productName}}",
        { integrationName: "Dsco", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ERP_WMS_3PL,
    status: { source: "gen3", kind: IntegrationKind.Dsco },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Dsco },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Dsco],
  },
];

const ecommercePlatforms: IntegrationCatalogEntry[] = [
  {
    slug: "shopify",
    name: "Shopify",
    get description() {
      return t(
        "Your {{integrationName}} store powers {{productName}} across every product",
        { integrationName: "Shopify", productName: "Redo" },
      );
    },
    products: [
      RedoProduct.RETURNS,
      RedoProduct.SUPPORT,
      RedoProduct.OMS,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.CUSTOMER_ACCOUNTS,
      RedoProduct.CONVERSION,
      RedoProduct.COVERAGE,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "platform", platform: "shopify" },
    action: { behavior: "readOnly" },
    permissions: [G],
    gen2Kinds: ["Shopify"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/shopify",
  },
  {
    slug: "aptos",
    name: "Aptos",
    get description() {
      return t("Sync {{integrationName}} retail orders into {{productName}}", {
        integrationName: "Aptos",
        productName: "Redo",
      });
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Aptos" },
    action: { behavior: "detailPanel" },
    permissions: [OF],
    gen2Kinds: ["Aptos"],
    gen3Kinds: [],
  },
  {
    slug: "rithum",
    name: "Rithum",
    get description() {
      return t(
        "Sync {{integrationName}} ({{integrationName2}}) dropship orders with {{productName}}",
        {
          integrationName: "Rithum",
          integrationName2: "CommerceHub",
          productName: "Redo",
        },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Rithum" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Rithum"],
    gen3Kinds: [],
  },
  {
    slug: "sps-commerce",
    name: "SPS Commerce",
    get description() {
      return t(
        "Sync {{integrationName}} purchase orders and shipments with {{productName}}",
        { integrationName: "SPS Commerce", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "SpsCommerce" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["SpsCommerce"],
    gen3Kinds: [],
    adminOnly: true,
    isBeta: true,
  },
  {
    slug: "amazon",
    name: "Amazon",
    get description() {
      return t(
        "Pull {{integrationName}} orders into {{productName}} fulfillment and buy shipping labels through {{integrationName2}}",
        {
          integrationName: "Amazon",
          productName: "Redo",
          integrationName2: "Amazon Buy Shipping",
        },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "AmazonSellingPartners" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["AmazonSellingPartners"],
    gen3Kinds: [],
  },
  {
    slug: "walmart",
    name: "Walmart",
    get description() {
      return t(
        "Pull {{integrationName}} marketplace orders into {{productName}} fulfillment",
        { integrationName: "Walmart", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Walmart" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Walmart"],
    gen3Kinds: [],
  },
  {
    slug: "ebay",
    name: "eBay",
    get description() {
      return t(
        "Pull {{integrationName}} orders into {{productName}} fulfillment",
        { integrationName: "eBay", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Ebay" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Ebay"],
    gen3Kinds: [],
  },
  {
    slug: "faire",
    name: "Faire",
    get description() {
      return t(
        "Pull {{integrationName}} wholesale orders into {{productName}} fulfillment",
        { integrationName: "Faire", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Faire" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Faire"],
    gen3Kinds: [],
  },
  {
    slug: "woocommerce",
    name: "WooCommerce",
    get description() {
      return t(
        "Pull {{integrationName}} orders into {{productName}} fulfillment",
        { integrationName: "WooCommerce", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "WooCommerce" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["WooCommerce"],
    gen3Kinds: [],
  },
  {
    slug: "wayfair",
    name: "Wayfair",
    get description() {
      return t(
        "Pull {{integrationName}} orders into {{productName}} fulfillment",
        { integrationName: "Wayfair", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Wayfair" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["Wayfair"],
    gen3Kinds: [],
  },
  {
    slug: "temu",
    name: "Temu",
    get description() {
      return t(
        "Pull {{integrationName}} orders into {{productName}} fulfillment",
        { integrationName: "Temu", productName: "Redo" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "UnifiedTemu" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["UnifiedTemu"],
    gen3Kinds: [],
    featureFlag: OmsFlagKey.TEMU_INTEGRATION,
  },
  {
    slug: "tiktok-shop",
    name: "TikTok Shop",
    get description() {
      return t(
        "Connect your {{integrationName}} to manage returns through {{productName}}",
        { integrationName: "TikTok Shop", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    // Not the hub key: that one stays returns-gated for the legacy page.
    status: { source: "gen2Kind", kind: "UnifiedTiktok" },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["OmsTiktok", "UnifiedTiktok"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/tiktok",
    isBeta: true,
  },
  {
    slug: "bigcommerce",
    name: "BigCommerce",
    get description() {
      return t(
        "{{integrationName}} order sync, set up by the {{productName}} team",
        { integrationName: "BigCommerce", productName: "Redo" },
      );
    },
    // One doc: storefront coverage extension, returns refunds/exchanges,
    // Recover cart triggers, order-tracking provider resolution, order sync.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.OMS,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.COVERAGE,
      RedoProduct.PLATFORM,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "Bigcommerce" },
    action: { behavior: "contactRedo" },
    permissions: [R, OF, M],
    gen2Kinds: ["Bigcommerce"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/bigcommerce",
    adminOnly: true,
  },
  {
    slug: "commerce-cloud",
    name: "Salesforce Commerce Cloud",
    get description() {
      return t(
        "{{integrationName}} order sync, set up by the {{productName}} team",
        { integrationName: "Commerce Cloud", productName: "Redo" },
      );
    },
    // Not OMS (marketplace provider is a stub): returns order sync, coverage
    // cartridge, and marketing segmentation via SFCC loyalty-tier reads.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.MARKETING,
      RedoProduct.COVERAGE,
      RedoProduct.PLATFORM,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    status: { source: "gen2Kind", kind: "CommerceCloud" },
    action: { behavior: "detailPanel" },
    permissions: [R, M],
    gen2Kinds: ["CommerceCloud"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/salesforce-commerce-cloud",
    adminOnly: true,
  },
  {
    slug: "comment-sold",
    name: "Comment Sold",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Comment Sold",
        productName: "Redo",
      });
    },
    // Coverage: order sync ingests Redo coverage charges; denied-claim PP+
    // refunds also run through this integration.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.OMS,
      RedoProduct.MARKETING,
      RedoProduct.COVERAGE,
    ],
    type: IntegrationTypeCategory.ECOMMERCE_PLATFORM,
    // Not the hub key: that one stays existence-based for the legacy page.
    status: { source: "gen2Kind", kind: "Commentsold" },
    action: { behavior: "activationOnly" },
    permissions: [R, OF, M],
    gen2Kinds: ["Commentsold"],
    gen3Kinds: [],
  },
];

const returnsServices: IntegrationCatalogEntry[] = [
  {
    slug: "loop",
    name: "Loop",
    get description() {
      return t(
        "Connect your {{integrationName}} returns account to {{productName}}",
        { integrationName: "Loop", productName: "Redo" },
      );
    },
    // Order tracking mints Loop return deeplinks for non-Redo-Returns teams.
    products: [RedoProduct.RETURNS, RedoProduct.ORDER_TRACKING],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.LOOP },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Loop"],
    gen3Kinds: [IntegrationKind.Loop],
    // Deliberately hidden from merchants (competitive sensitivity — Loop is a
    // competitor; Jordan/Will 2026-07-16). Do not remove adminOnly.
    adminOnly: true,
  },
  {
    slug: "narvar",
    name: "Narvar",
    get description() {
      return t(
        "Connect your {{integrationName}} returns account to {{productName}}",
        { integrationName: "Narvar", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.NARVAR },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Narvar"],
    gen3Kinds: [],
    adminOnly: true,
    isBeta: true,
  },
  {
    slug: "mirakl",
    name: "Mirakl",
    get description() {
      return t(
        "Import returns from {{integrationName}} marketplaces like {{marketplaceName}} and {{marketplaceName2}}",
        {
          integrationName: "Mirakl",
          marketplaceName: "Nordstrom",
          marketplaceName2: "Macy's",
        },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "gen3", kind: IntegrationKind.Mirakl },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Mirakl],
  },
  {
    slug: "amazon-returns",
    name: "Amazon Returns",
    get description() {
      return t(
        "Import seller-fulfilled {{integrationName}} returns so the warehouse can process them",
        { integrationName: "Amazon" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    // Shares the Amazon card's Selling Partner connection, which claims the kind.
    status: { source: "gen2Kind", kind: "AmazonSellingPartners" },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: [],
    gen3Kinds: [],
  },
  {
    slug: "return-logic",
    name: "ReturnLogic",
    get description() {
      return t(
        "Connect your {{integrationName}} returns account to {{productName}}",
        { integrationName: "ReturnLogic", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.RETURNLOGIC },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["ReturnLogic"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "inmar",
    name: "Inmar",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Inmar",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.INMAR },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Inmar"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/returns-services/inmar",
  },
  {
    slug: "idf-shipping",
    name: "IDF Shipping",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "IDF Shipping",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.IDF_SHIPPING },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["IDFShipping"],
    gen3Kinds: [],
  },
  {
    slug: "refundid",
    name: "Refundid",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Refundid",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.REFUNDID },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Refundid"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/refundid",
  },
  {
    slug: "purple-dot",
    name: "Purple Dot",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Purple Dot",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.PURPLE_DOT },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["PurpleDot"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/purple-dot",
  },
  {
    slug: "katana",
    name: "Katana",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Katana",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.KATANA },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Katana"],
    gen3Kinds: [],
  },
  {
    slug: "custom-return",
    get name() {
      return t("Custom Return Integration");
    },
    get description() {
      return t("Set up a custom return integration");
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.CUSTOM_RETURN },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["CustomReturnIntegration"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/ecommerce/custom",
  },
  {
    slug: "extensiv",
    name: "Extensiv",
    get description() {
      return t(
        "Connect your {{integrationName}} account to automatically sync return RMAs",
        { integrationName: "Extensiv (3PL Central)" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.EXTENSIV },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Extensiv"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/3pl-wms/extensiv",
  },
  {
    slug: "sfom",
    name: "SFOM",
    get description() {
      return t(
        "{{integrationName}} returns sync, set up by the {{productName}} team",
        { integrationName: "SFOM", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "statusKey", key: IntegrationStatusKey.SFOM },
    action: { behavior: "contactRedo" },
    permissions: [R],
    gen2Kinds: ["SFOM"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "happy-returns",
    name: "Happy Returns",
    get description() {
      return t(
        "{{integrationName}} dropoff, set up by the {{productName}} team",
        { integrationName: "Happy Returns", productName: "Redo" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "gen2Kind", kind: "HappyReturns" },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["HappyReturns"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "liquidonate",
    name: "LiquiDonate",
    get description() {
      return t(
        "Route eligible returns to a nearby nonprofit through {{integrationName}}",
        { integrationName: "LiquiDonate" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.RETURNS_SERVICE,
    status: { source: "gen2Kind", kind: "Liquidonate" },
    action: { behavior: "detailPanel" },
    permissions: [R],
    gen2Kinds: ["Liquidonate"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "yofi",
    name: "Yofi",
    get description() {
      return t(
        "{{integrationName}} fraud and bot detection, set up by the {{productName}} team",
        { integrationName: "Yofi", productName: "Redo" },
      );
    },
    // Returns consumes it: return flagging, fraud scores, claim charges.
    products: [RedoProduct.RETURNS, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "teamSetting", setting: "yofi" },
    action: { behavior: "contactRedo" },
    permissions: [R],
    gen2Kinds: [],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "falcon",
    name: "Redo Offer Network",
    get description() {
      return t("Show personalized offers on your tracking and thank-you pages");
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "gen2Kind", kind: "Falcon" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["Falcon"],
    gen3Kinds: [],
  },
  {
    slug: "rokt",
    name: "Rokt",
    get description() {
      return t(
        "Show personalized {{integrationName}} offers on your tracking page",
        { integrationName: "Rokt" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "gen2Kind", kind: "Rokt" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["Rokt"],
    gen3Kinds: [],
  },
  {
    slug: "uptick",
    name: "Uptick",
    get description() {
      return t(
        "Show personalized {{integrationName}} offers on your {{platformName}} thank-you and order status pages",
        { integrationName: "Uptick", platformName: "Shopify" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.OTHER,
    status: { source: "gen2Kind", kind: "Uptick" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    featureFlag: MarketingFlagKey.UPTICK_INTEGRATION,
    gen2Kinds: ["Uptick"],
    gen3Kinds: [],
  },
];

const smsEmailMarketing: IntegrationCatalogEntry[] = [
  {
    slug: "klaviyo",
    name: "Klaviyo",
    get description() {
      return t(
        "Connect your {{integrationName}} account to automatically sync customer events",
        { integrationName: "Klaviyo" },
      );
    },
    products: [
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.CUSTOMER_ACCOUNTS,
      RedoProduct.MARKETING,
      RedoProduct.COVERAGE,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.KLAVIYO },
    action: { behavior: "activationOnly" },
    permissions: [R, M, CA, G],
    gen2Kinds: ["Klaviyo"],
    gen3Kinds: [IntegrationKind.Klaviyo],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/klaviyo",
  },
  {
    slug: "cordial",
    name: "Cordial",
    get description() {
      return t(
        "Connect your {{integrationName}} account to automatically sync customer events",
        { integrationName: "Cordial" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.CORDIAL },
    action: {
      behavior: "settingsPage",
      route: "/settings/integrations/cordial",
    },
    permissions: [R],
    gen2Kinds: ["Cordial"],
    gen3Kinds: [],
  },
  {
    slug: "attentive",
    name: "Attentive",
    get description() {
      return t("Connect {{integrationName}} for SMS support and marketing", {
        integrationName: "Attentive",
      });
    },
    // Order tracking: the marketing-purpose doc's ORDER_TRACKING event drives
    // Attentive tracking notifications (tracking-event-handlers).
    products: [
      RedoProduct.RETURNS,
      RedoProduct.SUPPORT,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "Attentive" },
    action: { behavior: "activationOnly" },
    permissions: [R, S, M],
    gen2Kinds: ["Attentive"],
    gen3Kinds: [IntegrationKind.Attentive],
  },
  {
    slug: "postscript",
    name: "Postscript",
    get description() {
      return t("Connect {{integrationName}} for SMS support and marketing", {
        integrationName: "Postscript",
      });
    },
    // Order tracking: the same ORDER_TRACKING event registration the marketing
    // row toggles is what the order-tracking sender dispatches on.
    products: [
      RedoProduct.SUPPORT,
      RedoProduct.MARKETING,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "Postscript" },
    action: { behavior: "activationOnly" },
    permissions: [S, M],
    gen2Kinds: ["Postscript"],
    gen3Kinds: [IntegrationKind.Postscript],
  },
  {
    slug: "google-ads",
    name: "Google Ads",
    get description() {
      return t(
        "Connect your {{productName}} segments to your {{integrationName}} customer lists",
        { productName: "Redo", integrationName: "Google Ads" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "GoogleAds" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["GoogleAds"],
    gen3Kinds: [],
  },
  {
    slug: "meta-ads",
    name: "Meta Ads & Lead Forms",
    get description() {
      return t(
        "Sync {{productName}} segments to {{integrationName}} custom audiences and capture leads from {{integrationName}} forms",
        { productName: "Redo", integrationName: "Meta" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "MetaAds" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["MetaAds"],
    gen3Kinds: [],
  },
  {
    slug: "tiktok-ads",
    name: "TikTok Ads",
    get description() {
      return t(
        "Sync {{productName}} segments to {{integrationName}} custom audiences",
        { productName: "Redo", integrationName: "TikTok Ads" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "TiktokAds" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["TiktokAds"],
    gen3Kinds: [],
    featureFlag: MarketingFlagKey.TIKTOK_ADS_INTEGRATION,
  },
  {
    slug: "snapchat-ads",
    name: "Snapchat Ads",
    get description() {
      return t(
        "Send server-side conversion events to {{integrationName}} through {{integrationName2}}",
        { integrationName: "Snapchat Ads", integrationName2: "Contextual" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    // Contextual-only vendor: no Redo-native connection, so the card follows
    // the Contextual destination's status and only lists for Contextual teams.
    status: { source: "contextual", destination: "snapchat" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [],
    availabilitySetting: CatalogAvailabilitySetting.Contextual,
  },
  {
    slug: "orders-api",
    get name() {
      return t("Orders API");
    },
    get description() {
      return t(
        "Send order events to {{productName}} marketing via the Orders API",
        { productName: "Redo" },
      );
    },
    // The doc flips the team's primary provider to REDO_API: outbound
    // fulfillment enablement (oms) and return side effects ride on it.
    products: [RedoProduct.MARKETING, RedoProduct.OMS, RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "OrdersApi" },
    action: { behavior: "activationOnly" },
    permissions: [M, OF, R],
    gen2Kinds: ["OrdersApi"],
    gen3Kinds: [],
    docsUrl: "https://developers.redo.com/api-reference/orders/create-order",
  },
  {
    slug: "bubblehouse",
    name: "Bubblehouse",
    get description() {
      return t(
        "Award loyalty points via {{integrationName}} when a return is processed",
        { integrationName: "Bubblehouse" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.BUBBLEHOUSE },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Bubblehouse"],
    gen3Kinds: [],
  },
  {
    slug: "creditsyard",
    name: "CreditsYard",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "CreditsYard",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.CREDITSYARD },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["CreditsYard"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/creditsyard",
  },
  {
    slug: "rise-ai",
    name: "Rise.ai",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Rise.ai",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.RISE_AI },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["RiseAi"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/rise-ai",
  },
  {
    slug: "loopz",
    name: "Loopz",
    get description() {
      return t(
        "Connect your {{integrationName}} account to issue gift cards on returns",
        { integrationName: "Loopz" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "statusKey", key: IntegrationStatusKey.LOOPZ },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["Loopz"],
    gen3Kinds: [],
    isBeta: true,
  },
  {
    slug: "stay-ai",
    name: "Stay AI",
    get description() {
      return t("Sync {{integrationName}} subscriptions with {{productName}}", {
        integrationName: "Stay AI",
        productName: "Redo",
      });
    },
    // One API-key doc serves marketing sync, reclaim dispute evidence, returns
    // coverage cleanup, and the tracking page's subscription-upsell block.
    products: [
      RedoProduct.MARKETING,
      RedoProduct.RECLAIM,
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "StayAi" },
    action: { behavior: "activationOnly" },
    permissions: [M, G, R],
    gen2Kinds: ["StayAi"],
    gen3Kinds: [],
  },
  {
    slug: "growave",
    name: "Growave",
    get description() {
      return t(
        "Connect {{integrationName}} to enable marketing automation triggers",
        { integrationName: "Growave" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "Growave" },
    action: { behavior: "activationOnly" },
    permissions: [M],
    gen2Kinds: ["Growave"],
    gen3Kinds: [],
    // The legacy marketing card is commented out ("inactive for now"), so
    // merchants never had a Growave surface — staff-managed until it launches.
    adminOnly: true,
  },
  {
    slug: "google-merchant-center",
    name: "Google Merchant Center",
    get description() {
      return t("{{integrationName}} sync, set up by the {{productName}} team", {
        integrationName: "Google Merchant Center",
        productName: "Redo",
      });
    },
    // Consumers are the IMS product-listing feed, not marketing.
    products: [RedoProduct.OMS, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.SMS_EMAIL_MARKETING,
    status: { source: "gen2Kind", kind: "GoogleMerchantCenter" },
    action: { behavior: "contactRedo" },
    permissions: [OF],
    gen2Kinds: ["GoogleMerchantCenter"],
    gen3Kinds: [],
    adminOnly: true,
  },
];

const reviewsLoyaltySubscriptions: IntegrationCatalogEntry[] = [
  {
    slug: "judgeme",
    name: "Judge.me",
    get description() {
      return t(
        "Connect {{integrationName}} to sync product reviews with {{productName}}",
        { integrationName: "Judge.me", productName: "Redo" },
      );
    },
    // Reviews feed marketing review sync and the conversion checkout widget.
    products: [RedoProduct.MARKETING, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Judgeme },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Judgeme },
    permissions: [M, CO],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Judgeme],
  },
  {
    slug: "loyalty-lion",
    name: "LoyaltyLion",
    get description() {
      return t(
        "Connect {{integrationName}} to sync loyalty points with {{productName}}",
        { integrationName: "LoyaltyLion", productName: "Redo" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.LoyaltyLion },
    action: { behavior: "gen3Connect", kind: IntegrationKind.LoyaltyLion },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.LoyaltyLion],
  },
  {
    slug: "stamped",
    name: "Stamped",
    get description() {
      return t(
        "Connect {{integrationName}} to show product reviews in {{productName}} checkout",
        { integrationName: "Stamped", productName: "Redo" },
      );
    },
    // Reviews are read at checkout render time; there is no marketing sync.
    products: [RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Stamped },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Stamped },
    permissions: [CO],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Stamped],
  },
  {
    slug: "yotpo",
    name: "Yotpo",
    get description() {
      return t(
        "Connect {{integrationName}} to sync reviews and loyalty with {{productName}}",
        { integrationName: "Yotpo", productName: "Redo" },
      );
    },
    products: [RedoProduct.MARKETING, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Yotpo },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Yotpo },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Yotpo],
  },
  {
    slug: "okendo",
    name: "Okendo",
    get description() {
      return t(
        "Connect {{integrationName}} to sync reviews and loyalty with {{productName}}",
        { integrationName: "Okendo", productName: "Redo" },
      );
    },
    products: [RedoProduct.MARKETING, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    // AnyKind so a reviews-only (Okendo) merchant reads connected — otherwise
    // the flag-off visibility gate strands them off the Okendo page entirely.
    status: { source: "gen2AnyKind", kinds: ["Okendo", "OkendoMarketing"] },
    action: { behavior: "activationOnly" },
    permissions: [M, CO],
    gen2Kinds: ["Okendo", "OkendoMarketing"],
    gen3Kinds: [],
    featureFlag: MarketingFlagKey.OKENDO_MARKETING_INTEGRATION,
  },
  {
    slug: "smile",
    name: "Smile.io",
    get description() {
      return t(
        "Connect {{integrationName}} to surface loyalty points in {{productName}} messaging",
        { integrationName: "Smile.io", productName: "Redo" },
      );
    },
    products: [RedoProduct.MARKETING],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.Smile },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Smile },
    permissions: [M],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Smile],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/smile",
  },
  {
    slug: "recharge",
    name: "Recharge",
    get description() {
      return t("Sync {{integrationName}} subscriptions with {{productName}}", {
        integrationName: "Recharge",
        productName: "Redo",
      });
    },
    // One credential doc serves marketing upsells, support subscription
    // management, returns-side coverage cleanup, tracking-page upsells, and
    // dispute evidence.
    products: [
      RedoProduct.MARKETING,
      RedoProduct.SUPPORT,
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen2Kind", kind: "Recharge" },
    action: { behavior: "activationOnly" },
    permissions: [M, S, R, G],
    // Legacy parity: Recharge connected from the marketing-settings page or the
    // support channels card, so the Returns row admits either.
    productPermissions: { [RedoProduct.RETURNS]: [M, S] },
    gen2Kinds: ["Recharge"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/recharge",
  },
  {
    slug: "skio",
    name: "Skio",
    get description() {
      return t("Sync {{integrationName}} subscriptions with {{productName}}", {
        integrationName: "Skio",
        productName: "Redo",
      });
    },
    // One API-key doc serves marketing sync, returns-side coverage cleanup,
    // the tracking page's subscription-upsell block, and dispute evidence.
    products: [
      RedoProduct.MARKETING,
      RedoProduct.RETURNS,
      RedoProduct.ORDER_TRACKING,
      RedoProduct.RECLAIM,
    ],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen2Kind", kind: "Skio" },
    action: { behavior: "activationOnly" },
    permissions: [M, R, G],
    // Legacy parity: the sole Skio connect surface was the marketing-settings
    // page, so the Returns row reuses that gate instead of 403ing on its RPC.
    productPermissions: { [RedoProduct.RETURNS]: [M] },
    gen2Kinds: ["Skio"],
    gen3Kinds: [],
  },
  {
    slug: "loop-subscriptions",
    get name() {
      return t("{{integrationName}} Subscriptions", {
        integrationName: "Loop",
      });
    },
    get description() {
      return t(
        "Use {{integrationName}} subscription history as {{productName}} dispute evidence",
        { integrationName: "Loop", productName: "Redo" },
      );
    },
    // Read-only: the token is read on demand per dispute, with no sync.
    products: [RedoProduct.RECLAIM],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "gen3", kind: IntegrationKind.LoopSubscriptions },
    action: {
      behavior: "gen3Connect",
      kind: IntegrationKind.LoopSubscriptions,
    },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.LoopSubscriptions],
  },
  {
    slug: "rivo",
    name: "Rivo",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Rivo",
        productName: "Redo",
      });
    },
    products: [RedoProduct.CUSTOMER_ACCOUNTS, RedoProduct.RETURNS],
    type: IntegrationTypeCategory.REVIEWS_LOYALTY_SUBSCRIPTIONS,
    status: { source: "statusKey", key: IntegrationStatusKey.RIVO },
    action: { behavior: "activationOnly" },
    permissions: [CA, R],
    gen2Kinds: ["Rivo"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/marketing-loyalty/rivo",
  },
];

const supportChannels: IntegrationCatalogEntry[] = [
  {
    slug: "support-email",
    get name() {
      return t("Email");
    },
    get description() {
      return t(
        "Connect {{integrationName}} or {{integrationName2}} to answer support email in {{productName}}",
        {
          integrationName: "Gmail",
          integrationName2: "Outlook",
          productName: "Redo",
        },
      );
    },
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: {
      source: "gen2AnyKind",
      kinds: ["Gmail", "Outlook"],
      orSetting: "customSenderEmails",
    },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/supportEmail",
      connectRoute: "/settings/channels/email-connect",
    },
    permissions: [S],
    gen2Kinds: ["Gmail", "Outlook"],
    gen3Kinds: [],
  },
  {
    slug: "facebook-messenger",
    name: "Facebook Messenger",
    get description() {
      return t(
        "Answer {{integrationName}} DMs and comments from {{productName}} support",
        { integrationName: "Facebook", productName: "Redo" },
      );
    },
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "gen2AnyKind", kinds: ["Facebook", "Instagram"] },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/meta-settings",
      connectRoute: "/settings/channels/meta-connect",
    },
    permissions: [S],
    gen2Kinds: ["Facebook"],
    gen3Kinds: [],
  },
  {
    slug: "instagram",
    name: "Instagram",
    get description() {
      return t(
        "Answer {{integrationName}} DMs and comments from {{productName}} support",
        { integrationName: "Instagram", productName: "Redo" },
      );
    },
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "gen2AnyKind", kinds: ["Facebook", "Instagram"] },
    // Instagram shares Meta's connect + settings surfaces with Facebook.
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/meta-settings",
      connectRoute: "/settings/channels/meta-connect",
    },
    permissions: [S],
    gen2Kinds: ["Instagram"],
    gen3Kinds: [],
  },
  {
    slug: "tiktok-support",
    name: "TikTok Messaging",
    get description() {
      return t(
        "Answer {{integrationName}} messages from {{productName}} support",
        { integrationName: "TikTok", productName: "Redo" },
      );
    },
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "gen2Kind", kind: "Tiktok" },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/tiktok-settings",
    },
    permissions: [S],
    gen2Kinds: ["Tiktok"],
    gen3Kinds: [],
  },
  {
    slug: "chat-widget",
    get name() {
      return t("Custom website chat widget");
    },
    get description() {
      return t(
        "Chat with shoppers on your storefront through {{productName}} support",
        { productName: "Redo" },
      );
    },
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "teamSetting", setting: "customerWidget" },
    action: { behavior: "settingsPage", route: "/settings/customer-widget" },
    permissions: [S],
    gen2Kinds: [],
    gen3Kinds: [],
  },
  {
    slug: "sms-voice",
    get name() {
      return t("SMS/Voice");
    },
    get description() {
      return t(
        "Support shoppers over text and phone calls in {{productName}}",
        { productName: "Redo" },
      );
    },
    products: [RedoProduct.SUPPORT],
    type: IntegrationTypeCategory.MESSAGING_CHANNEL,
    status: { source: "teamSetting", setting: "supportPhone" },
    action: {
      behavior: "settingsPage",
      route: "/settings/channels/voice-settings",
      connectRoute: "/settings/channels/phone-setup",
    },
    permissions: [S],
    gen2Kinds: [],
    gen3Kinds: [],
  },
  {
    slug: "gorgias",
    name: "Gorgias",
    get description() {
      return t(
        "Show returns in {{integrationName}} tickets, escalate support tickets, and pull ticket history for dispute evidence",
        { integrationName: "Gorgias" },
      );
    },
    // One shared connection: Returns sidebar widget + Support escalation +
    // Reclaim dispute evidence + Recover outreach suppression/handoff.
    products: [
      RedoProduct.RETURNS,
      RedoProduct.SUPPORT,
      RedoProduct.RECLAIM,
      RedoProduct.MARKETING,
    ],
    type: IntegrationTypeCategory.HELPDESK,
    status: { source: "gen2Kind", kind: "Gorgias" },
    action: { behavior: "activationOnly" },
    permissions: [R, S, G, M],
    // Legacy parity: the Recover card showed this status to marketing or
    // support, matching getGorgiasConnectionStatus's own gate.
    productPermissions: { [RedoProduct.MARKETING]: [M, S] },
    gen2Kinds: ["Gorgias"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/gorgias",
  },
  {
    slug: "kustomer",
    name: "Kustomer",
    get description() {
      return t(
        "{{integrationName}} helpdesk sync, set up by the {{productName}} team",
        { productName: "Redo", integrationName: "Kustomer" },
      );
    },
    products: [RedoProduct.SUPPORT, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.HELPDESK,
    status: { source: "gen2Kind", kind: "Kustomer" },
    action: { behavior: "contactRedo" },
    permissions: [S],
    gen2Kinds: ["Kustomer"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "richpanel",
    name: "Richpanel",
    get description() {
      return t(
        "Escalate support conversations to {{integrationName}} over SMS",
        { integrationName: "Richpanel" },
      );
    },
    // Recover SMS conversations (marketing) escalate to Richpanel and resume
    // on ticket resolution via the same connection.
    products: [RedoProduct.SUPPORT, RedoProduct.MARKETING],
    type: IntegrationTypeCategory.HELPDESK,
    status: { source: "gen2Kind", kind: "Richpanel" },
    action: { behavior: "activationOnly" },
    permissions: [S, M],
    // Same as Gorgias: the Recover card's status was readable by marketing or
    // support, while connecting always meant the support-gated connect page.
    productPermissions: { [RedoProduct.MARKETING]: [M, S] },
    gen2Kinds: ["Richpanel"],
    gen3Kinds: [],
  },
];

const paymentsFinance: IntegrationCatalogEntry[] = [
  {
    slug: "authorizenet",
    name: "Authorize.net",
    get description() {
      return t("Connect your {{integrationName}} account to process refunds", {
        integrationName: "Authorize.net",
      });
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "statusKey", key: IntegrationStatusKey.AUTHORIZENET },
    action: { behavior: "activationOnly" },
    permissions: [R],
    gen2Kinds: ["AuthorizeNet"],
    gen3Kinds: [],
  },
  {
    slug: "chargeflow",
    name: "Chargeflow",
    get description() {
      return t(
        "Connect {{integrationName}} to compare your pre-chargeback alert volume against {{productName}}",
        { integrationName: "Chargeflow", productName: "Resolve" },
      );
    },
    products: [RedoProduct.RECLAIM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen3", kind: IntegrationKind.Chargeflow },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Chargeflow },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Chargeflow],
    // Only RESOLVE reads this alert volume, so without the flag connecting
    // would store a vendor API key that nothing ever calls.
    featureFlag: ReclaimFlagKey.DISPUTE_RESOLVE_UI,
  },
  {
    slug: "stripe",
    name: "Stripe",
    get description() {
      return t(
        "Connect {{integrationName}} to manage payment disputes in {{productName}}",
        { integrationName: "Stripe", productName: "Redo" },
      );
    },
    products: [RedoProduct.RECLAIM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    // Disputes connect/sync still runs on the Gen II Mongo doc; the Gen III
    // config exists but nothing writes its credentials yet.
    status: { source: "gen2Kind", kind: "Stripe" },
    action: { behavior: "activationOnly" },
    permissions: [G],
    gen2Kinds: ["Stripe"],
    gen3Kinds: [IntegrationKind.Stripe],
  },
  {
    slug: "paypal",
    name: "PayPal",
    get description() {
      return t(
        "Connect {{integrationName}} to manage payment disputes in {{productName}}",
        { integrationName: "PayPal", productName: "Redo" },
      );
    },
    // Reclaim (disputes) surface + return/denied-claim refund consumption +
    // IMS agentic-commerce payment capture (oms).
    products: [
      RedoProduct.RETURNS,
      RedoProduct.COVERAGE,
      RedoProduct.RECLAIM,
      RedoProduct.OMS,
    ],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen2Kind", kind: "PayPal" },
    action: { behavior: "activationOnly" },
    permissions: [G],
    gen2Kinds: ["PayPal"],
    gen3Kinds: [],
  },
  {
    slug: "adyen",
    name: "Adyen",
    get description() {
      return t(
        "{{integrationName}} payments, set up by the {{productName}} team",
        { integrationName: "Adyen", productName: "Redo" },
      );
    },
    // Refunds both return charges and denied-coverage-claim PP+ fees.
    products: [RedoProduct.RETURNS, RedoProduct.COVERAGE, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen2Kind", kind: "Adyen" },
    action: { behavior: "contactRedo" },
    permissions: [G],
    gen2Kinds: ["Adyen"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "optty",
    name: "Optty",
    get description() {
      return t(
        "{{integrationName}} payments, set up by the {{productName}} team",
        { productName: "Redo", integrationName: "Optty" },
      );
    },
    // Refunds both return charges and denied-coverage-claim PP+ fees.
    products: [RedoProduct.RETURNS, RedoProduct.COVERAGE, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen2Kind", kind: "Optty" },
    action: { behavior: "contactRedo" },
    permissions: [G],
    gen2Kinds: ["Optty"],
    gen3Kinds: [],
    adminOnly: true,
  },
  {
    slug: "disputely",
    name: "Disputely",
    get description() {
      return t(
        "{{integrationName}} dispute automation, powering {{productName}} deflections",
        { integrationName: "Disputely", productName: "RESOLVE" },
      );
    },
    products: [RedoProduct.RECLAIM, RedoProduct.PLATFORM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen3", kind: IntegrationKind.Disputely },
    action: { behavior: "activationOnly" },
    permissions: [G],
    gen2Kinds: ["Disputely"],
    gen3Kinds: [IntegrationKind.Disputely],
    // Same gate the legacy RESOLVE surface used: the card is hidden unless the
    // flag is on, and stays reachable for teams already enrolled.
    featureFlag: ReclaimFlagKey.DISPUTE_RESOLVE_UI,
  },
  {
    slug: "chargeblast",
    name: "Chargeblast",
    get description() {
      return t(
        "Connect {{integrationName}} to compare your pre-chargeback alert volume against {{productName}}",
        { integrationName: "Chargeblast", productName: "Resolve" },
      );
    },
    products: [RedoProduct.RECLAIM],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen3", kind: IntegrationKind.Chargeblast },
    action: { behavior: "gen3Connect", kind: IntegrationKind.Chargeblast },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Chargeblast],
    // Only RESOLVE reads this alert volume, so without the flag connecting
    // would store a vendor API key that nothing ever calls.
    featureFlag: ReclaimFlagKey.DISPUTE_RESOLVE_UI,
  },
  {
    slug: "quickbooks-online",
    name: "QuickBooks Online",
    get description() {
      return t("Connect {{integrationName}} to sync inventory and accounting", {
        integrationName: "QuickBooks Online",
      });
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.PAYMENTS_FINANCE,
    status: { source: "gen3", kind: IntegrationKind.QuickBooksOnline },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.QuickBooksOnline],
    featureFlag: OmsFlagKey.IMS_QUICKBOOKS,
  },
];

const analytics: IntegrationCatalogEntry[] = [
  {
    slug: "google-analytics",
    name: "Google Analytics",
    get description() {
      return t("Connect {{integrationName}} to optimize for AI search", {
        integrationName: "Google Analytics",
      });
    },
    products: [RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.ANALYTICS,
    status: { source: "gen3", kind: IntegrationKind.GoogleAnalytics },
    action: { behavior: "gen3Connect", kind: IntegrationKind.GoogleAnalytics },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.GoogleAnalytics],
  },
  {
    slug: "google-search-console",
    name: "Google Search Console",
    get description() {
      return t("Connect {{integrationName}} to optimize for AI search", {
        integrationName: "Google Search Console",
      });
    },
    products: [RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.ANALYTICS,
    status: { source: "gen3", kind: IntegrationKind.GoogleSearchConsole },
    action: {
      behavior: "gen3Connect",
      kind: IntegrationKind.GoogleSearchConsole,
    },
    permissions: [G],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.GoogleSearchConsole],
  },
  {
    slug: "triple-whale",
    name: "Triple Whale",
    get description() {
      return t("Send {{productName}} data to {{integrationName}} analytics", {
        productName: "Redo",
        integrationName: "Triple Whale",
      });
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.ANALYTICS,
    status: { source: "gen3", kind: IntegrationKind.TripleWhale },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.TripleWhale],
  },
];

const crossBorder: IntegrationCatalogEntry[] = [
  {
    slug: "globale",
    name: "Global-e",
    get description() {
      return t("Connect your {{integrationName}} account to {{productName}}", {
        integrationName: "Global-e",
        productName: "Redo",
      });
    },
    products: [RedoProduct.RETURNS, RedoProduct.OMS],
    type: IntegrationTypeCategory.CROSS_BORDER,
    status: { source: "statusKey", key: IntegrationStatusKey.GLOBALE },
    action: { behavior: "activationOnly" },
    permissions: [R, OF],
    gen2Kinds: ["GlobalE"],
    gen3Kinds: [],
    docsUrl:
      "https://developers.redo.com/docs/integrations/returns/other/global-e",
  },
  {
    slug: "landmark-global",
    name: "Landmark Global",
    get description() {
      return t("Cross-border fulfillment through {{integrationName}}", {
        integrationName: "Landmark Global",
      });
    },
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CROSS_BORDER,
    status: { source: "gen2Kind", kind: "LandmarkGlobal" },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: ["LandmarkGlobal", "RedoLandmarkGlobal"],
    gen3Kinds: [],
  },
  {
    slug: "zonos",
    name: "Zonos",
    get description() {
      return t(
        "Connect your {{integrationName}} account so OMS can fulfill orders from {{integrationName}} Checkout",
        { integrationName: "Zonos" },
      );
    },
    products: [RedoProduct.OMS],
    type: IntegrationTypeCategory.CROSS_BORDER,
    status: { source: "gen3", kind: IntegrationKind.Zonos },
    action: { behavior: "activationOnly" },
    permissions: [OF],
    gen2Kinds: [],
    gen3Kinds: [IntegrationKind.Zonos],
    availabilitySetting: CatalogAvailabilitySetting.MerchantZonos,
  },
];

/** Self-serve carrier brands; status folds all the brand's account variants
 * (EasyPost-brokered and standalone) via connectedCarrierBrands. */
const merchantCarrier = (
  slug: CarrierBrandSlug,
  name: string,
  products: readonly RedoProduct[] = [RedoProduct.OMS, RedoProduct.RETURNS],
  gen2Kinds: readonly Gen2IntegrationKind[] = [],
): IntegrationCatalogEntry => ({
  slug,
  name,
  get description() {
    return t(
      "Connect your {{integrationName}} carrier account for shipping labels",
      { integrationName: name },
    );
  },
  products,
  type: IntegrationTypeCategory.CARRIER,
  status: { source: "carrier", brand: slug },
  action: { behavior: "detailPanel" },
  permissions: [SH],
  gen2Kinds,
  gen3Kinds: [],
});

const carriers: IntegrationCatalogEntry[] = [
  merchantCarrier(
    "amazon-shipping",
    "Amazon Shipping",
    [RedoProduct.OMS, RedoProduct.RETURNS],
    ["AmazonShippingCarrierAccount"],
  ),
  merchantCarrier("apc", "APC"),
  merchantCarrier("asendia", "Asendia"),
  merchantCarrier("australia-post", "Australia Post"),
  merchantCarrier("canpar-express", "Canpar Express"),
  merchantCarrier("dhl-express", "DHL Express"),
  merchantCarrier(
    "ecom-express",
    "Ecom Express",
    [RedoProduct.OMS, RedoProduct.CONVERSION],
    ["EcomExpressCarrierAccount"],
  ),
  merchantCarrier("epost-global", "ePost Global"),
  merchantCarrier("eta-express", "ETA Express", [
    RedoProduct.OMS,
    RedoProduct.CONVERSION,
  ]),
  merchantCarrier(
    "freightwire",
    "FreightWire",
    [RedoProduct.OMS, RedoProduct.CONVERSION],
    ["FreightWireCarrierAccount"],
  ),
  merchantCarrier("gls", "GLS"),
  merchantCarrier("gofo", "GOFO"),
  merchantCarrier("jitsu", "Jitsu", [RedoProduct.ORDER_TRACKING]),
  merchantCarrier("ontrac", "OnTrac"),
  merchantCarrier("p2p-global", "Point2Point Global"),
  merchantCarrier("passport-global", "Passport Global", [RedoProduct.OMS]),
  merchantCarrier("purolator", "Purolator"),
  merchantCarrier("royal-mail", "Royal Mail"),
  merchantCarrier("seko-omniparcel", "OmniParcel - Seko"),
  merchantCarrier("spee-dee", "SpeeDee"),
  merchantCarrier("veho", "Veho"),
  {
    slug: "parent-easypost",
    name: "EasyPost",
    get description() {
      return t("Connect your {{integrationName}} account for outbound labels", {
        integrationName: "EasyPost",
      });
    },
    // Order tracking: the doc's webhookSecret authenticates EasyPost tracker
    // webhooks, the source of this team's tracker events.
    products: [
      RedoProduct.OMS,
      RedoProduct.RETURNS,
      RedoProduct.CONVERSION,
      RedoProduct.ORDER_TRACKING,
    ],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "gen2Kind", kind: "ParentEasypost" },
    // Account-level connect lives on the OMS activation row; Returns rides it.
    action: { behavior: "activationOnly" },
    permissions: [OF],
    // Status-only row: EasyPost tracking has no settings surface of its own,
    // so the account's own permission reads it rather than marketing settings.
    productPermissions: { [RedoProduct.ORDER_TRACKING]: [OF] },
    gen2Kinds: ["ParentEasypost"],
    gen3Kinds: [],
  },
  {
    slug: "fedex",
    name: "FedEx",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "FedEx" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "fedex" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["FedExCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "usps",
    name: "USPS",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "USPS" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "usps" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["UspsCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "uniuni",
    name: "UniUni",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "UniUni" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "uniuni" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["UniUniCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "dhl-ecommerce",
    name: "DHL eCommerce",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "DHL eCommerce" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "dhl-ecommerce" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["DhlEcommerceDirectCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "sendcloud",
    name: "Sendcloud",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for return labels",
        { integrationName: "Sendcloud" },
      );
    },
    products: [RedoProduct.RETURNS],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "sendcloud" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["SendcloudCarrierAccount", "SendcloudCarrierContract"],
    gen3Kinds: [],
  },
  {
    slug: "stallion-express",
    name: "Stallion Express",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "Stallion Express" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "stallion-express" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["StallionExpressCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "chitchats",
    name: "ChitChats",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "ChitChats" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "chitchats" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["ChitChatsCarrierAccount"],
    gen3Kinds: [],
  },
  {
    slug: "canada-post",
    name: "Canada Post",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "Canada Post" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.RETURNS, RedoProduct.CONVERSION],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "canada-post" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: [
      "CanadaPostCarrierAccount",
      "CanadaPostDeveloperPortalCarrierAccount",
    ],
    gen3Kinds: [],
  },
  {
    slug: "ups",
    name: "UPS",
    get description() {
      return t(
        "Connect your {{integrationName}} carrier account for shipping labels",
        { integrationName: "UPS" },
      );
    },
    products: [RedoProduct.OMS, RedoProduct.RETURNS],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "carrier", brand: "ups" },
    action: { behavior: "detailPanel" },
    permissions: [SH],
    gen2Kinds: ["UpsGhostAccountIntegration"],
    gen3Kinds: [],
  },
  {
    slug: "easypost-carrier",
    get name() {
      return t("{{integrationName}} Carrier Accounts", {
        integrationName: "EasyPost",
      });
    },
    get description() {
      return t(
        "Child {{integrationName}} carrier accounts, set up by the {{productName}} team",
        { integrationName: "EasyPost", productName: "Redo" },
      );
    },
    products: [
      RedoProduct.OMS,
      RedoProduct.RETURNS,
      RedoProduct.CONVERSION,
      RedoProduct.PLATFORM,
    ],
    type: IntegrationTypeCategory.CARRIER,
    status: { source: "gen2Kind", kind: "Easypost" },
    action: { behavior: "contactRedo" },
    permissions: [G],
    gen2Kinds: ["Easypost"],
    gen3Kinds: [],
    adminOnly: true,
  },
];

export const INTEGRATIONS_CATALOG: readonly IntegrationCatalogEntry[] = [
  ...erpWms3pl,
  ...ecommercePlatforms,
  ...returnsServices,
  ...smsEmailMarketing,
  ...reviewsLoyaltySubscriptions,
  ...supportChannels,
  ...paymentsFinance,
  ...analytics,
  ...crossBorder,
  ...carriers,
];

/** Any-of hub/vendor route gate: the union of every permission that grants at
 * least one catalog card, so anyone who can see a card can reach the page. */
export const INTEGRATIONS_HUB_PERMISSIONS: readonly UserPermissions[] = [
  ...new Set(INTEGRATIONS_CATALOG.flatMap((entry) => entry.permissions)),
];

/** Route-permission source for deep pages: keeps card visibility and page access in lockstep. */
export function catalogRoutePermission(slug: string): PermissionHandle {
  const entry = INTEGRATIONS_CATALOG.find(
    (candidate) => candidate.slug === slug,
  );
  if (!entry) {
    // Runs at module scope while the route tree is built: a throw would
    // white-screen the app, and [] reads as "no gate" — so deny this route.
    console.error(`No integrations catalog entry for slug: ${slug}`);
    return () => false;
  }
  return [...entry.permissions];
}
